import { loadFromSupabase } from "../supabaseDB";

/* GFI Nexus — shared helpers for Forecast & Market Intelligence modules
   that consume api_settings (fix-dashboard.md "Fase C"). Single source
   for the key-reveal logic (shared with ApiSettingsRegistry.js — see
   that file's header for why base64 here is obfuscation, not real
   encryption) and the "load this API's config" + "not configured"
   notice pattern every Fase C module needs. */

export function obfuscateKey(plain) {
  if (!plain) return "";
  try { return btoa(unescape(encodeURIComponent(plain))); } catch (e) { return plain; }
}
export function revealKey(encoded) {
  if (!encoded) return "";
  try { return decodeURIComponent(escape(atob(encoded))); } catch (e) { return encoded; }
}

/* APIs that work with no key at all (public endpoints) — Google News
   RSS, Frankfurter (currency; the api_name slug stays
   "exchangerate_host" even though the underlying provider is now
   Frankfurter — see ApiSettingsRegistry.js's DEFAULT_APIS comment on
   why the slug wasn't renamed). Everything else needs a non-empty
   api_key AND enabled=true to count as "configured". NewsData.io
   (slug "gdelt" — see fetchNewsDataDirect below) requires a key,
   unlike the GDELT provider it replaced, so it is NOT in this map. */
var NO_AUTH_APIS = { google_news_rss: true, exchangerate_host: true };

/* Resolves one API's config row. Returns
   { configured, enabled, apiKey, endpointUrl } — `configured` means
   "this data source is ready to call": enabled=true, plus a non-empty
   key for anything in the auth-required set above. Never throws —
   network/parse failures resolve to the "not configured" shape so
   callers can render the standard notice instead of crashing. */
export async function loadApiSetting(apiName) {
  try {
    var rows = await loadFromSupabase("api_settings");
    var row = (rows || []).find(function (r) { return r.id === apiName; });
    if (!row) return { configured: false, enabled: false, apiKey: "", endpointUrl: "" };
    var needsKey = !NO_AUTH_APIS[apiName];
    return {
      configured: !!row.enabled && (!needsKey || !!row.apiKey),
      enabled: !!row.enabled,
      apiKey: revealKey(row.apiKey || ""),
      endpointUrl: row.endpointUrl || "",
    };
  } catch (e) {
    console.warn("[apiSettingsHelpers] loadApiSetting(" + apiName + ") failed:", e && e.message);
    return { configured: false, enabled: false, apiKey: "", endpointUrl: "" };
  }
}

/* Calls netlify/functions/api-proxy.js in mode:"fetch" and returns the
   parsed JSON body. Every Fase C module's external data call goes
   through this now (fix-dashboard.md "Bug CORS al llamar Frankfurter
   ... Asegurar que todas las llamadas API externas pasen por el proxy
   Netlify") — server-to-server has no CORS to worry about, so no
   external API's browser-CORS support has to be assumed or discovered
   the hard way again. `path` is everything after the API's base URL
   (e.g. "/weather?lat=19.5&lon=-102.4&units=metric") — the proxy
   appends the resolved key itself for APIs that need one, so the key
   never has to sit in a URL string built on the frontend. `apiKey` is
   only needed here for a not-yet-saved-in-Supabase key the user just
   typed into a Test Connection-style flow; normally the proxy's own
   env-var-first / Supabase-row resolution covers it and this can be
   omitted. `opts.endpointUrl` forwards the per-row custom endpoint
   from Settings (falls back to the proxy's own hardcoded default when
   omitted). Throws on failure — callers already wrap this in their
   own try/catch per fix-dashboard.md's earlier crash-defensiveness
   pass. */
export async function fetchViaProxy(apiName, path, opts) {
  opts = opts || {};
  var res = await fetch("/.netlify/functions/api-proxy", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ apiName: apiName, mode: "fetch", path: path, apiKey: opts.apiKey || "", endpointUrl: opts.endpointUrl || "" }),
  });
  var payload = await res.json();
  if (!payload.ok) throw new Error(payload.error || payload.message || ("HTTP " + payload.statusCode));
  return JSON.parse(payload.body);
}

/* NewsData.io direct-from-browser fetch (fix-dashboard.md: "Reemplazar
   GDELT Project por NewsData.io ... CORS habilitado"). This is the
   provider that replaced GDELT in this slot — the api_name slug stays
   "gdelt" (see ApiSettingsRegistry.js's DEFAULT_APIS comment on why a
   provider swap doesn't rename the slug: it would orphan an
   already-configured Supabase row). NewsData.io has CORS enabled for
   browser calls, same as GDELT did, so this stays a direct fetch
   rather than routing through the Netlify proxy. `path` is everything
   after the base URL, apikey included (e.g.
   "/latest?apikey=...&q=...&language=en,es&size=10&from_date=...").
   A single attempt, 15s timeout — NewsData's free tier responds fast;
   unlike GDELT this doesn't need retry/backoff since the Netlify-side
   connectivity issue that motivated GDELT's retry logic doesn't apply
   here (this call never touches Netlify Functions). Validates
   data.status === "success" in addition to HTTP ok, since NewsData.io
   can return a 200 with an error status body. */
export async function fetchNewsDataDirect(path, opts) {
  opts = opts || {};
  var base = opts.endpointUrl || "https://newsdata.io/api/1";
  var url = base + path;
  var controller = new AbortController();
  var timer = setTimeout(function () { controller.abort(); }, 15000);
  try {
    var res = await fetch(url, { signal: controller.signal });
    clearTimeout(timer);
    if (!res.ok) throw new Error("HTTP " + res.status);
    var data = await res.json();
    if (data.status !== "success") throw new Error(data.message || (data.results && data.results.message) || "NewsData.io returned status: " + data.status);
    return data;
  } catch (e) {
    clearTimeout(timer);
    var reason = e && e.name === "AbortError" ? "timeout" : (e && e.message ? e.message : String(e));
    throw new Error("NewsData.io request failed (" + reason + ")");
  }
}

var NOTICE_T = {
  en: { configure: "Configure", inSettings: "in Settings → API Keys", goTo: "Go to API Settings" },
  es: { configure: "Configura", inSettings: "en Settings → API Keys", goTo: "Ir a API Settings" },
};

/* "Configure {apiDisplayName} in Settings → API Keys" notice + button
   that navigates there via the window.__gfiNavigate cross-module hook
   (App.js — same mechanism every other cross-module handoff in this
   app uses, per CLAUDE.md). */
export function ApiNotConfiguredNotice({ apiDisplayName, lang }) {
  var t = NOTICE_T[lang === "es" ? "es" : "en"];
  return (
    <div style={{ padding: "16px 20px", borderRadius: 10, background: "rgba(107,114,128,0.08)", border: "1px dashed var(--color-border)", textAlign: "center" }}>
      <div style={{ fontSize: 24, marginBottom: 8 }}>⏱</div>
      <div style={{ fontSize: 13, color: "var(--color-text-secondary)", marginBottom: 10 }}>
        {t.configure} {apiDisplayName} {t.inSettings}
      </div>
      <button
        type="button"
        onClick={function () { if (window.__gfiNavigate) window.__gfiNavigate("api_settings"); }}
        style={{ padding: "7px 16px", borderRadius: 6, border: "none", background: "var(--color-accent)", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 700 }}
      >
        {t.goTo}
      </button>
    </div>
  );
}
